<template>
  <div class="gm">
    <!-- Login (eigenes Firebase-Projekt ohmyart-ai) -->
    <div v-if="!authUser && !initializing" class="gm__login">
      <div class="gm__loginCard">
        <div class="gm__loginTitle"><v-icon color="#7b5cd6" class="mr-2">mdi-pulse</v-icon>Generation Monitor</div>
        <div class="gm__loginHint">Login für das Projekt ohmyart-ai</div>
        <v-text-field v-model="loginEmail" label="E-Mail" outlined dense rounded hide-details class="mb-2" prepend-inner-icon="mdi-email-outline" />
        <v-text-field v-model="loginPassword" label="Passwort" type="password" outlined dense rounded hide-details prepend-inner-icon="mdi-lock-outline" @keyup.enter="handleLogin" />
        <div v-if="authError" class="gm__loginError">{{ authError }}</div>
        <v-btn color="primary" depressed rounded block class="mt-4" :loading="authLoading" @click="handleLogin">Anmelden</v-btn>
      </div>
    </div>

    <template v-if="authUser">
      <!-- Kopfzeile -->
      <div class="gm__top">
        <div class="gm__title">
          <v-icon color="#7b5cd6" class="mr-2">mdi-pulse</v-icon>
          <b>Generation Monitor</b>
          <span class="gm__live" :class="{ 'gm__live--on': liveActive }"><span class="gm__dot"></span>{{ liveActive ? "Live" : "Archiv" }}</span>
          <span v-if="lastEventAgo" class="gm__muted">letzte Generierung {{ lastEventAgo }}</span>
        </div>
        <v-spacer />
        <div class="gm__day">
          <button type="button" class="gm__dayBtn" title="Vortag" @click="shiftDay(-1)"><v-icon small>mdi-chevron-left</v-icon></button>
          <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <button type="button" class="gm__dayLabel" v-bind="attrs" v-on="on"><v-icon small class="mr-1">mdi-calendar</v-icon>{{ dayLabel }}</button>
            </template>
            <v-date-picker v-model="selectedDate" no-title scrollable :max="todayKey" @input="dateMenu = false" />
          </v-menu>
          <button type="button" class="gm__dayBtn" :disabled="selectedDate >= todayKey" title="Folgetag" @click="shiftDay(1)"><v-icon small>mdi-chevron-right</v-icon></button>
          <button v-if="selectedDate !== todayKey" type="button" class="gm__dayToday" @click="selectedDate = todayKey">Heute</button>
        </div>
        <v-btn icon small class="ml-2" title="Abmelden" @click="handleLogout"><v-icon small>mdi-logout</v-icon></v-btn>
      </div>

      <!-- Warnungen (letzte 60 Minuten) -->
      <div v-if="alerts.length" class="gm__alerts">
        <div v-for="a in alerts" :key="a.key" class="gm__alert" :class="'gm__alert--' + a.level">
          <v-icon small class="mr-2">{{ a.level === "error" ? "mdi-alert-octagon" : "mdi-alert" }}</v-icon>
          <span>{{ a.text }}</span>
          <button v-if="a.filter" type="button" class="gm__alertBtn" @click="applyFilter(a.filter)">anzeigen</button>
        </div>
      </div>

      <!-- Kennzahlen -->
      <div class="gm__kpis">
        <div v-for="k in kpis" :key="k.label" class="gm__kpi" :class="k.tone ? 'gm__kpi--' + k.tone : ''" @click="k.filter && applyFilter(k.filter)">
          <div class="gm__kpiLabel">{{ k.label }}</div>
          <div class="gm__kpiValue">{{ k.value }}<span v-if="k.unit" class="gm__kpiUnit">{{ k.unit }}</span></div>
          <div class="gm__kpiFoot">
            <span v-if="k.delta !== null && k.delta !== undefined" class="gm__delta" :class="deltaClass(k)">{{ k.delta > 0 ? "+" : "" }}{{ k.delta }}{{ k.deltaUnit || "" }} <span class="gm__muted">vs {{ prevLabel }}</span></span>
            <span v-else class="gm__muted">{{ k.foot || "" }}</span>
          </div>
        </div>
      </div>

      <!-- Stundenverlauf + Verteilung -->
      <div class="gm__row">
        <div class="gm__card gm__chart">
          <div class="gm__cardHead"><b>Verlauf nach Stunde</b><span class="gm__muted">Erfolg · Fallback · Fehler · abgewiesen — Gesamtzahlen exakt</span></div>
          <div class="gm__bars">
            <div v-for="h in hours" :key="h.hour" class="gm__hour" :title="`${h.hour}:00 · ${h.total} Generierungen · ${h.error} Fehler · ${h.warn} Fallback · ${h.rejected} abgewiesen`" @click="applyFilter({ hour: h.hour })">
              <div class="gm__hourStack" :style="{ height: h.pct + '%' }">
                <div class="gm__seg gm__seg--rejected" :style="{ flex: h.rejected }"></div>
                <div class="gm__seg gm__seg--error" :style="{ flex: h.error }"></div>
                <div class="gm__seg gm__seg--warn" :style="{ flex: h.warn }"></div>
                <div class="gm__seg gm__seg--ok" :style="{ flex: h.success }"></div>
              </div>
              <div class="gm__hourLbl" :class="{ 'gm__hourLbl--now': h.hour === currentHour && liveActive }">{{ h.hour % 3 === 0 ? h.hour : "" }}</div>
            </div>
          </div>
        </div>
        <div class="gm__card gm__dist">
          <div class="gm__cardHead"><b>Verteilung</b><span class="gm__muted">Stichprobe der letzten {{ sample.length }} + alle Probleme · Klick filtert</span></div>
          <div v-for="g in distributions" :key="g.key" class="gm__distGroup">
            <div class="gm__distTitle">{{ g.title }}</div>
            <div class="gm__distChips">
              <button v-for="d in g.items" :key="d.key" type="button" class="gm__distChip" :class="{ 'gm__distChip--bad': d.errPct >= 10, 'gm__distChip--on': filters[g.key] === d.key }" :title="`${d.count} Generierungen · ${d.errPct} % Fehler/Fallback`" @click="toggleFilter(g.key, d.key)">
                <span class="gm__distName">{{ d.label }}</span><span class="gm__distCount">{{ d.count }}</span><span v-if="d.errPct" class="gm__distErr">{{ d.errPct }} %</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Fehler-Cluster -->
      <div v-if="clusters.length" class="gm__card gm__clusters">
        <div class="gm__cardHead">
          <b>Fehler-Cluster</b><span class="gm__muted">{{ clusters.length }} Muster · {{ clusterTotal }} Vorfälle</span>
          <v-spacer />
          <button type="button" class="gm__linkBtn" @click="clustersOpen = !clustersOpen">{{ clustersOpen ? "einklappen" : "ausklappen" }}</button>
        </div>
        <div v-if="clustersOpen" class="gm__clusterList">
          <button v-for="c in clusters" :key="c.key" type="button" class="gm__cluster" :class="{ 'gm__cluster--on': filters.cluster === c.key }" @click="toggleFilter('cluster', c.key)">
            <span class="gm__pill" :class="'gm__pill--' + c.status">{{ statusLabel(c.status) }}</span>
            <span class="gm__clusterCode">{{ c.code || "—" }}</span>
            <span class="gm__clusterMsg">{{ c.message }}</span>
            <span class="gm__clusterMeta">{{ c.count }}× · {{ c.engines.join(", ") || "—" }} · zuerst {{ fmtTime(c.first) }} · zuletzt {{ fmtTime(c.last) }}</span>
          </button>
        </div>
      </div>

      <!-- Filter -->
      <div class="gm__filters">
        <div class="gm__segmented">
          <button v-for="s in statusOptions" :key="s.value" type="button" class="gm__segBtn" :class="{ 'gm__segBtn--on': filters.status === s.value }" @click="filters.status = s.value">{{ s.label }}<span class="gm__segCount">{{ countByStatus(s.value) }}</span></button>
        </div>
        <v-text-field v-model="filters.search" dense outlined rounded hide-details clearable placeholder="Nutzer, Request-ID, Fehlertext, Engine …" prepend-inner-icon="mdi-magnify" class="gm__search" />
        <div class="gm__activeFilters">
          <span v-for="f in activeFilterChips" :key="f.key" class="gm__chip gm__chip--filter">{{ f.label }}<button type="button" class="gm__chipX" @click="clearFilter(f.key)">×</button></span>
          <button v-if="activeFilterChips.length" type="button" class="gm__linkBtn" @click="resetFilters">alle zurücksetzen</button>
        </div>
        <span class="gm__muted gm__count">{{ filteredLogs.length }} geladen<template v-if="filters.status === 'all' || filters.status === 'success'"> · Stichprobe, ältere Erfolge über das Datum</template></span>
      </div>

      <!-- Kacheln -->
      <div v-if="loading" class="gm__loading"><v-progress-circular indeterminate size="28" color="#7b5cd6" /></div>
      <div v-else-if="!filteredLogs.length" class="gm__empty">Keine Generierungen für diese Auswahl.</div>
      <div v-else class="gm__grid">
        <div v-for="item in visibleLogs" :key="item.id" class="gm__tile" :class="['gm__tile--' + item.status, { 'gm__tile--new': item._fresh }]" @click="openDetail(item)">
          <div class="gm__tileHead">
            <span class="gm__pill" :class="'gm__pill--' + item.status">{{ statusLabel(item.status) }}</span>
            <span class="gm__chip">{{ item.styleId || "?" }} <b>v{{ item.version || "1" }}</b><template v-if="item.theme && item.theme !== 'default'"> · {{ item.theme }}</template></span>
            <span class="gm__chip" :class="{ 'gm__chip--warn': item.fallback }" :title="item.fallback ? 'Fallback-Engine (erste Engine gescheitert)' : ''">{{ shortEngine(item.engineId) }}<v-icon v-if="item.fallback" x-small class="ml-1">mdi-swap-horizontal</v-icon></span>
            <v-spacer />
            <span class="gm__time">{{ fmtTime(item.ts) }}</span>
          </div>
          <div class="gm__tileImgs">
            <div class="gm__inputs" :class="'gm__inputs--' + Math.min(4, inputUrls(item).length)">
              <img v-for="(u, i) in inputUrls(item).slice(0, 4)" :key="i" :src="u" loading="lazy" alt="" @click.stop="openImage(u)" />
              <span v-if="item.combined" class="gm__combined">{{ inputUrls(item).length }} Fotos</span>
            </div>
            <v-icon small class="gm__arrow">mdi-arrow-right</v-icon>
            <div class="gm__result" :class="{ 'gm__result--dark': item.cutout && item.cutout.state === 'done' }" @click.stop="item.resultImageUrl && openImage(item.resultImageUrl)">
              <img v-if="item.resultImageUrl" :src="item.resultImageUrl" loading="lazy" alt="" />
              <div v-else class="gm__noResult"><v-icon color="rgba(0,0,0,.3)">mdi-image-off-outline</v-icon><span>{{ item.status === "rejected" ? "abgewiesen" : "kein Bild" }}</span></div>
            </div>
          </div>
          <div v-if="item.status !== 'success'" class="gm__tileErr">{{ item.errorCode ? item.errorCode + ": " : "" }}{{ item.error }}</div>
          <div class="gm__tileFoot">
            <span v-if="item.durationMs" class="gm__meta" :title="`Generierung ${fmtMs(item.generationMs)} · Freisteller ${fmtMs(item.cutoutMs)}`"><v-icon x-small>mdi-timer-outline</v-icon>{{ fmtMs(item.durationMs) }}</span>
            <span v-if="item.cutout" class="gm__meta" :class="{ 'gm__meta--bad': item.cutout.state === 'failed' }" :title="cutoutTitle(item)"><v-icon x-small>mdi-content-cut</v-icon>{{ item.cutout.state === "done" ? "freigestellt" : item.cutout.state === "failed" ? "Freisteller-Fehler" : item.cutout.state }}</span>
            <span v-if="item.usage" class="gm__meta" :class="{ 'gm__meta--bad': item.usage.count >= item.usage.max }" title="Tageslimit des Nutzers"><v-icon x-small>mdi-counter</v-icon>{{ item.usage.count }}/{{ item.usage.max }}</span>
            <span v-if="item.origin" class="gm__meta" :title="item.origin">{{ hostOf(item.origin) }}</span>
            <v-spacer />
            <button type="button" class="gm__user" :title="item.userId" @click.stop="toggleFilter('user', item.userId)"><v-icon x-small>mdi-account-outline</v-icon>{{ shortId(item.userId) }}<span v-if="userCounts[item.userId] > 1" class="gm__userN">{{ userCounts[item.userId] }}</span></button>
          </div>
        </div>
      </div>
      <div v-if="filteredLogs.length > visibleCount" class="gm__more"><v-btn text rounded color="#7b5cd6" @click="visibleCount += 60">{{ filteredLogs.length - visibleCount }} weitere laden</v-btn></div>

      <!-- Detail -->
      <v-navigation-drawer v-model="drawer" right temporary fixed width="760" class="gm__drawer">
        <div v-if="detail" class="gm__detail">
          <div class="gm__detailHead">
            <span class="gm__pill" :class="'gm__pill--' + detail.status">{{ statusLabel(detail.status) }}</span>
            <b>{{ detail.styleId }} v{{ detail.version || "1" }}</b>
            <span v-if="detail.theme" class="gm__muted">· {{ detail.theme }}</span>
            <span class="gm__muted">· {{ fmtDateTime(detail.ts) }}</span>
            <v-spacer />
            <v-btn icon small @click="drawer = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>

          <div v-if="detail.status !== 'success'" class="gm__detailErr"><b>{{ detail.errorCode || "Fehler" }}</b><span>{{ detail.error }}</span></div>

          <div class="gm__detailImgs">
            <div class="gm__detailCol">
              <div class="gm__detailLbl">Eingabe{{ inputUrls(detail).length > 1 ? ` (${inputUrls(detail).length} Fotos)` : "" }}</div>
              <div class="gm__detailInputs"><img v-for="(u, i) in inputUrls(detail)" :key="i" :src="u" alt="" @click="openImage(u)" /></div>
            </div>
            <div class="gm__detailCol">
              <div class="gm__detailLbl">
                Ergebnis
                <span v-if="detail.opaqueImageUrl" class="gm__segmented gm__segmented--mini">
                  <button type="button" class="gm__segBtn" :class="{ 'gm__segBtn--on': resultView === 'final' }" @click="resultView = 'final'">freigestellt</button>
                  <button type="button" class="gm__segBtn" :class="{ 'gm__segBtn--on': resultView === 'opaque' }" @click="resultView = 'opaque'">opak</button>
                </span>
                <span v-if="detail.cutout && detail.cutout.state === 'done'" class="gm__segmented gm__segmented--mini ml-1">
                  <button type="button" class="gm__segBtn" :class="{ 'gm__segBtn--on': resultBg === 'dark' }" @click="resultBg = 'dark'">dunkel</button>
                  <button type="button" class="gm__segBtn" :class="{ 'gm__segBtn--on': resultBg === 'light' }" @click="resultBg = 'light'">hell</button>
                </span>
              </div>
              <div class="gm__detailResult" :class="'gm__detailResult--' + resultBg" @click="detailResultUrl && openImage(detailResultUrl)">
                <img v-if="detailResultUrl" :src="detailResultUrl" alt="" />
                <div v-else class="gm__noResult"><v-icon color="rgba(0,0,0,.3)">mdi-image-off-outline</v-icon></div>
              </div>
            </div>
          </div>

          <!-- Versuche -->
          <div class="gm__section">
            <div class="gm__sectionTitle">Ablauf<span class="gm__muted" v-if="detail.durationMs">gesamt {{ fmtMs(detail.durationMs) }}<template v-if="detail.generationMs"> · Generierung {{ fmtMs(detail.generationMs) }}</template><template v-if="detail.cutoutMs"> · Freisteller {{ fmtMs(detail.cutoutMs) }}</template></span></div>
            <div v-if="detail.attempts && detail.attempts.length" class="gm__timeline">
              <div v-for="(a, i) in detail.attempts" :key="i" class="gm__step" :class="a.ok ? 'gm__step--ok' : 'gm__step--fail'">
                <span class="gm__stepDot"></span>
                <span class="gm__stepEngine">{{ a.engine }}</span>
                <span class="gm__muted">{{ fmtMs(a.ms) }}</span>
                <span v-if="!a.ok" class="gm__stepErr">{{ a.code ? a.code + " · " : "" }}{{ a.error }}</span>
                <span v-else class="gm__stepOk">Bild erhalten</span>
              </div>
              <div v-if="detail.cutout" class="gm__step" :class="detail.cutout.state === 'failed' ? 'gm__step--fail' : 'gm__step--ok'">
                <span class="gm__stepDot"></span>
                <span class="gm__stepEngine">Freisteller {{ detail.cutout.method || "" }}</span>
                <span class="gm__muted">{{ fmtMs(detail.cutoutMs) }}</span>
                <span :class="detail.cutout.state === 'failed' ? 'gm__stepErr' : 'gm__stepOk'">{{ detail.cutout.state }}</span>
              </div>
            </div>
            <div v-else class="gm__muted">Für ältere Einträge liegt kein Ablauf vor.<template v-if="detail.engineId"> Engine: {{ detail.engineId }}</template></div>
          </div>

          <!-- Freisteller-Statistik -->
          <div v-if="detail.cutout && cutoutRows(detail).length" class="gm__section">
            <div class="gm__sectionTitle">Freisteller</div>
            <div class="gm__statGrid">
              <div v-for="r in cutoutRows(detail)" :key="r.k" class="gm__stat"><span class="gm__statK">{{ r.k }}</span><span class="gm__statV" :class="{ 'gm__statV--bad': r.bad }">{{ r.v }}</span></div>
            </div>
          </div>

          <!-- Kontext -->
          <div class="gm__section">
            <div class="gm__sectionTitle">Kontext</div>
            <div class="gm__statGrid">
              <div class="gm__stat"><span class="gm__statK">Nutzer</span><span class="gm__statV gm__mono">{{ detail.userId }}</span></div>
              <div v-if="detail.requestId" class="gm__stat"><span class="gm__statK">Request</span><span class="gm__statV gm__mono">{{ detail.requestId }}</span></div>
              <div v-if="detail.docId" class="gm__stat"><span class="gm__statK">Artwork-Doc</span><span class="gm__statV gm__mono">{{ detail.docId }}</span></div>
              <div v-if="detail.usage" class="gm__stat"><span class="gm__statK">Tageslimit</span><span class="gm__statV">{{ detail.usage.count }} von {{ detail.usage.max }}</span></div>
              <div v-if="detail.origin" class="gm__stat"><span class="gm__statK">Shop</span><span class="gm__statV">{{ hostOf(detail.origin) }}</span></div>
              <div v-if="detail.aspectRatio" class="gm__stat"><span class="gm__statK">Format</span><span class="gm__statV">{{ detail.aspectRatio }}</span></div>
              <div v-if="detail.finishReason" class="gm__stat"><span class="gm__statK">Finish</span><span class="gm__statV">{{ detail.finishReason }}</span></div>
              <div class="gm__stat"><span class="gm__statK">Warenkorb</span><span class="gm__statV">{{ cartState }}</span></div>
            </div>
          </div>

          <!-- Sitzung des Nutzers -->
          <div v-if="sessionLogs.length > 1" class="gm__section">
            <div class="gm__sectionTitle">Dieser Nutzer heute<span class="gm__muted">{{ sessionLogs.length }} Generierungen</span></div>
            <div class="gm__session">
              <button v-for="s in sessionLogs" :key="s.id" type="button" class="gm__sessionItem" :class="['gm__sessionItem--' + s.status, { 'gm__sessionItem--on': s.id === detail.id }]" :title="`${fmtTime(s.ts)} · ${s.styleId} v${s.version || 1} · ${statusLabel(s.status)}`" @click="openDetail(s)">
                <img v-if="s.resultImageUrl" :src="s.resultImageUrl" alt="" />
                <span v-else class="gm__sessionNo"><v-icon x-small>mdi-close</v-icon></span>
                <span class="gm__sessionLbl">{{ fmtTime(s.ts) }}</span>
              </button>
            </div>
          </div>

          <!-- Safety -->
          <div v-if="detail.safetyRatings && detail.safetyRatings.length" class="gm__section">
            <div class="gm__sectionTitle">KI-Sicherheitsbewertung</div>
            <div class="gm__safety"><span v-for="r in detail.safetyRatings" :key="r.category" class="gm__chip" :class="'gm__safety--' + (r.probability || '').toLowerCase()">{{ fmtCategory(r.category) }} · {{ r.probability }}</span></div>
          </div>

          <!-- Prompt -->
          <div v-if="detail.prompt" class="gm__section">
            <button type="button" class="gm__linkBtn" @click="promptOpen = !promptOpen">{{ promptOpen ? "Prompt ausblenden" : "Prompt anzeigen" }}</button>
            <pre v-if="promptOpen" class="gm__prompt">{{ detail.prompt }}</pre>
          </div>
        </div>
      </v-navigation-drawer>

      <v-dialog v-model="imageDialog" max-width="92vw" content-class="gm__imgDialog">
        <div class="gm__imgWrap" @click="imageDialog = false"><img :src="activeImg" alt="" /></div>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import { initializeApp, getApp, getApps } from "firebase/app";
import { getFirestore, collection, getDocs, onSnapshot, query, where, limit, orderBy, getCountFromServer, Timestamp } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";

const TZ = "Europe/Berlin";
const dayKeyOf = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
const hourOf = (d) => Number(new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "2-digit", hour12: false }).format(d));
const STATUS = { success: "Erfolg", warn: "Fallback", error: "Fehler", rejected: "Abgewiesen" };

/** Tages-Docs der Logs heißen nach UTC-Datum (Server-Zeit). Ein Berliner Tag liegt in zwei UTC-Docs (Vortag + Tag) → beide laden und nach Berliner Datum filtern */
const utcKeysFor = (dateKey) => {
  const d = new Date(dateKey + "T12:00:00Z");
  const prev = new Date(d);
  prev.setUTCDate(prev.getUTCDate() - 1);
  return [prev, d].map((x) => x.toISOString().slice(0, 10).replace(/-/g, "_"));
};

/** Berliner Tagesgrenzen als Zeitstempel */
const dayBounds = (dateKey) => {
  const noon = new Date(dateKey + "T12:00:00Z").getTime();
  const offsetAt = (ms) => {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date(ms));
    const h = Number(parts.find((p) => p.type === "hour").value) % 24;
    return (h - 12) * 3600000; // Offset Berlin gegen UTC um 12:00 UTC
  };
  const start = new Date(dateKey + "T00:00:00Z").getTime() - offsetAt(noon);
  return { start, end: start + 24 * 3600000 };
};
const SAMPLE_LIMIT = 400;
const ISSUE_STATUSES = ["warn", "error", "rejected"];

/**
 * Generation Monitor: Tageslogs aus generations/{YYYY_MM_DD}/logs (ohmyart-ai).
 * Bei ~7000 Logs pro Tag wird nicht alles geladen: Kacheln = Stichprobe der letzten SAMPLE_LIMIT je UTC-Doc,
 * Probleme (warn/error/rejected) vollständig, Gesamt- und Stundenzahlen per Aggregat-Abfrage. Heute live.
 */
export default {
  name: "GenerationMonitor",
  data() {
    return {
      initializing: true,
      authUser: null,
      authLoading: false,
      authError: "",
      loginEmail: "",
      loginPassword: "",
      loading: false,
      dateMenu: false,
      selectedDate: dayKeyOf(new Date()),
      sample: [],
      issues: [],
      totals: { total: 0, byHour: Array(24).fill(0), avgMs: 0 },
      prevTotals: { total: 0, issues: 0 },
      unsubscribe: null,
      liveBuckets: {},
      issueBuckets: {},
      aggTimer: null,
      nowTick: Date.now(),
      tickTimer: null,
      filters: { status: "all", styleId: null, version: null, engine: null, cutout: null, user: null, cluster: null, hour: null, search: "" },
      clustersOpen: true,
      visibleCount: 60,
      drawer: false,
      detail: null,
      resultView: "final",
      resultBg: "dark",
      promptOpen: false,
      cartState: "…",
      userSession: [],
      imageDialog: false,
      activeImg: "",
      seenIds: new Set(),
      statusOptions: [
        { label: "Alle", value: "all" },
        { label: "Erfolg", value: "success" },
        { label: "Fallback", value: "warn" },
        { label: "Fehler", value: "error" },
        { label: "Abgewiesen", value: "rejected" },
      ],
    };
  },
  computed: {
    todayKey() {
      return dayKeyOf(new Date());
    },
    liveActive() {
      return this.selectedDate === this.todayKey && !!this.unsubscribe;
    },
    dayLabel() {
      const d = new Date(this.selectedDate + "T12:00:00");
      return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
    },
    prevLabel() {
      return this.selectedDate === this.todayKey ? "gestern" : "Vortag";
    },
    currentHour() {
      return hourOf(new Date(this.nowTick));
    },
    lastEventAgo() {
      if (!this.liveActive || !this.logs.length) return "";
      const last = Math.max(...this.logs.map((l) => l.ts || 0));
      const s = Math.max(0, Math.round((this.nowTick - last) / 1000));
      return s < 60 ? `vor ${s} s` : s < 3600 ? `vor ${Math.round(s / 60)} min` : `vor ${Math.round(s / 3600)} h`;
    },
    /** Stichprobe + alle Probleme, ohne Doppelte, neueste zuerst */
    logs() {
      const seen = new Set();
      const out = [];
      for (const l of [...this.issues, ...this.sample]) {
        if (seen.has(l.id)) continue;
        seen.add(l.id);
        out.push(l);
      }
      return out.sort((a, b) => (b.ts || 0) - (a.ts || 0));
    },
    userCounts() {
      const m = {};
      for (const l of this.logs) m[l.userId] = (m[l.userId] || 0) + 1;
      return m;
    },
    issueCounts() {
      const c = { warn: 0, error: 0, rejected: 0, cutoutFailed: 0 };
      for (const l of this.issues) {
        if (c[l.status] !== undefined) c[l.status]++;
        if (l.cutout && l.cutout.state === "failed") c.cutoutFailed++;
      }
      return c;
    },
    /** Kennzahlen des Tages mit Vergleich zum Vortag */
    kpis() {
      const total = this.totals.total;
      const ic = this.issueCounts;
      const smp = this.summarize(this.sample);
      const pct = (n, t) => (t ? Math.round((n / t) * 1000) / 10 : 0);
      const errRate = pct(ic.error, total);
      const prevErrRate = pct(this.prevTotals.errors, this.prevTotals.total);
      return [
        { label: "Generierungen", value: total, delta: total - this.prevTotals.total, filter: { status: "all" } },
        { label: "Fehlerquote", value: errRate, unit: " %", delta: Math.round((errRate - prevErrRate) * 10) / 10, deltaUnit: " Pkt.", invert: true, tone: errRate >= 5 ? "bad" : errRate >= 2 ? "warn" : "good", filter: { status: "error" } },
        { label: "Fallbacks", value: ic.warn, delta: ic.warn - this.prevTotals.warn, invert: true, tone: ic.warn ? "warn" : "", filter: { status: "warn" } },
        { label: "Freisteller-Fehler", value: ic.cutoutFailed, foot: "aus allen Problemen", tone: ic.cutoutFailed ? "bad" : "", filter: { cutout: "failed" } },
        { label: "Abgewiesen (Limit)", value: ic.rejected, delta: ic.rejected - this.prevTotals.rejected, invert: true, filter: { status: "rejected" } },
        { label: "Dauer Median", value: smp.p50 ? this.fmtMs(smp.p50) : "–", foot: smp.p95 ? `p95 ${this.fmtMs(smp.p95)} · Stichprobe` : "nur neue Logs (V9)", tone: smp.p95 > 90000 ? "warn" : "" },
        { label: "Nutzer", value: smp.users, foot: `in Stichprobe (${this.sample.length})` },
        { label: "Kombi-Fotos", value: smp.combined, foot: "in Stichprobe, 2–4 Fotos" },
      ];
    },
    hours() {
      const rows = Array.from({ length: 24 }, (_, hour) => ({ hour, total: this.totals.byHour[hour] || 0, success: 0, warn: 0, error: 0, rejected: 0 }));
      for (const l of this.issues) {
        if (!l.ts) continue;
        rows[hourOf(new Date(l.ts))][l.status]++;
      }
      for (const r of rows) r.success = Math.max(0, r.total - r.warn - r.error - r.rejected);
      const max = Math.max(1, ...rows.map((r) => r.total));
      return rows.map((r) => ({ ...r, pct: Math.round((r.total / max) * 100) }));
    },
    distributions() {
      const build = (key, labelFn) => {
        const m = {};
        for (const l of this.logs) {
          const k = key === "version" ? String(l.version || 1) : key === "engine" ? l.engineId || "–" : key === "cutout" ? (l.cutout ? l.cutout.state : "ohne") : l[key] || "–";
          const e = (m[k] ||= { key: k, label: labelFn ? labelFn(k) : k, count: 0, bad: 0 });
          e.count++;
          if (l.status !== "success") e.bad++;
        }
        return Object.values(m)
          .map((e) => ({ ...e, errPct: Math.round((e.bad / e.count) * 100) }))
          .sort((a, b) => b.count - a.count);
      };
      return [
        { key: "styleId", title: "Stil", items: build("styleId") },
        { key: "version", title: "Version", items: build("version", (k) => "v" + k) },
        { key: "engine", title: "Engine", items: build("engine", (k) => this.shortEngine(k)) },
        { key: "cutout", title: "Freisteller", items: build("cutout") },
      ];
    },
    /** Gleiche Fehlermeldungen bündeln (Zahlen/IDs normalisiert) */
    clusters() {
      const m = {};
      for (const l of this.logs) {
        if (l.status === "success") continue;
        const msg = this.normalizeError(l.error || "");
        const key = `${l.status}|${l.errorCode || ""}|${msg}`;
        const c = (m[key] ||= { key, status: l.status, code: l.errorCode, message: msg || "(ohne Meldung)", count: 0, first: Infinity, last: 0, engines: new Set() });
        c.count++;
        c.first = Math.min(c.first, l.ts || Infinity);
        c.last = Math.max(c.last, l.ts || 0);
        if (l.engineId) c.engines.add(this.shortEngine(l.engineId));
      }
      return Object.values(m)
        .map((c) => ({ ...c, engines: [...c.engines] }))
        .sort((a, b) => b.count - a.count);
    },
    clusterTotal() {
      return this.clusters.reduce((s, c) => s + c.count, 0);
    },
    /** Warnungen aus den letzten 60 Minuten (nur live) */
    alerts() {
      if (!this.liveActive) return [];
      const since = this.nowTick - 3600000;
      const recent = this.logs.filter((l) => l.ts >= since);
      const out = [];
      if (recent.length >= 5) {
        const err = recent.filter((l) => l.status === "error").length;
        const rate = (err / recent.length) * 100;
        if (rate >= 5) out.push({ key: "err", level: rate >= 15 ? "error" : "warn", text: `Fehlerquote letzte Stunde ${Math.round(rate)} % (${err} von ${recent.length})`, filter: { status: "error" } });
        const warn = recent.filter((l) => l.status === "warn").length;
        if (warn / recent.length >= 0.1) out.push({ key: "warn", level: "warn", text: `${warn} Fallbacks in der letzten Stunde`, filter: { status: "warn" } });
        const cf = recent.filter((l) => l.cutout && l.cutout.state === "failed").length;
        if (cf >= 2) out.push({ key: "cut", level: "warn", text: `${cf} Freisteller-Fehler in der letzten Stunde`, filter: { cutout: "failed" } });
        const durs = recent.map((l) => l.durationMs).filter(Boolean).sort((a, b) => a - b);
        if (durs.length >= 5 && durs[Math.floor(durs.length * 0.95)] > 90000) out.push({ key: "slow", level: "warn", text: `Langsam: p95 ${this.fmtMs(durs[Math.floor(durs.length * 0.95)])} in der letzten Stunde` });
      }
      const rej = recent.filter((l) => l.status === "rejected").length;
      if (rej >= 3) out.push({ key: "rej", level: "warn", text: `${rej} Rate-Limit-Abweisungen in der letzten Stunde`, filter: { status: "rejected" } });
      if (this.logs.length && this.nowTick - Math.max(...this.logs.map((l) => l.ts || 0)) > 45 * 60000 && this.currentHour >= 8 && this.currentHour <= 23) {
        out.push({ key: "quiet", level: "warn", text: "Seit über 45 Minuten keine Generierung – läuft der Editor?" });
      }
      return out;
    },
    filteredLogs() {
      const f = this.filters;
      const q = (f.search || "").trim().toLowerCase();
      return this.logs
        .filter((l) => {
          if (f.status !== "all" && l.status !== f.status) return false;
          if (f.styleId && l.styleId !== f.styleId) return false;
          if (f.version && String(l.version || 1) !== f.version) return false;
          if (f.engine && (l.engineId || "–") !== f.engine) return false;
          if (f.cutout && (l.cutout ? l.cutout.state : "ohne") !== f.cutout) return false;
          if (f.user && l.userId !== f.user) return false;
          if (f.hour !== null && f.hour !== undefined && (!l.ts || hourOf(new Date(l.ts)) !== f.hour)) return false;
          if (f.cluster && `${l.status}|${l.errorCode || ""}|${this.normalizeError(l.error || "")}` !== f.cluster) return false;
          if (q) {
            const hay = `${l.userId} ${l.requestId || ""} ${l.error || ""} ${l.errorCode || ""} ${l.engineId || ""} ${l.styleId || ""} ${l.docId || ""}`.toLowerCase();
            if (!hay.includes(q)) return false;
          }
          return true;
        })
        .sort((a, b) => (b.ts || 0) - (a.ts || 0));
    },
    visibleLogs() {
      return this.filteredLogs.slice(0, this.visibleCount);
    },
    activeFilterChips() {
      const f = this.filters;
      const chips = [];
      if (f.styleId) chips.push({ key: "styleId", label: `Stil ${f.styleId}` });
      if (f.version) chips.push({ key: "version", label: `v${f.version}` });
      if (f.engine) chips.push({ key: "engine", label: this.shortEngine(f.engine) });
      if (f.cutout) chips.push({ key: "cutout", label: `Freisteller ${f.cutout}` });
      if (f.user) chips.push({ key: "user", label: `Nutzer ${this.shortId(f.user)}` });
      if (f.hour !== null && f.hour !== undefined) chips.push({ key: "hour", label: `${f.hour}:00 Uhr` });
      if (f.cluster) chips.push({ key: "cluster", label: "Fehler-Cluster" });
      return chips;
    },
    sessionLogs() {
      if (!this.detail) return [];
      if (this.userSession.length) return this.userSession;
      return this.logs.filter((l) => l.userId === this.detail.userId).sort((a, b) => (a.ts || 0) - (b.ts || 0));
    },
    detailResultUrl() {
      if (!this.detail) return "";
      return this.resultView === "opaque" && this.detail.opaqueImageUrl ? this.detail.opaqueImageUrl : this.detail.resultImageUrl;
    },
  },
  watch: {
    selectedDate() {
      this.visibleCount = 60;
      this.filters.hour = null;
      this.subscribe();
    },
    filters: {
      deep: true,
      handler() {
        this.visibleCount = 60;
      },
    },
  },
  mounted() {
    this.initAuthObserver();
    this.tickTimer = setInterval(() => (this.nowTick = Date.now()), 15000);
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
    clearInterval(this.tickTimer);
    clearInterval(this.aggTimer);
  },
  methods: {
    getIsolatedFirebase() {
      const firebaseConfig = {
        apiKey: "AIzaSyAV8jI0EekE4OMuClaQiF4WpuJeqnZ2AMA",
        authDomain: "ohmyart-ai.firebaseapp.com",
        projectId: "ohmyart-ai",
        storageBucket: "ohmyart-ai.firebasestorage.app",
        messagingSenderId: "3734662653",
        appId: "1:3734662653:web:f087ef05b947f3c218fd94",
      };
      const name = "gen_monitor_app";
      const app = getApps().find((a) => a.name === name) ? getApp(name) : initializeApp(firebaseConfig, name);
      return { auth: getAuth(app), db: getFirestore(app) };
    },
    initAuthObserver() {
      const { auth } = this.getIsolatedFirebase();
      onAuthStateChanged(auth, (user) => {
        this.authUser = user;
        this.initializing = false;
        if (user) this.subscribe();
      });
    },
    async handleLogin() {
      if (!this.loginEmail || !this.loginPassword) return;
      this.authLoading = true;
      this.authError = "";
      try {
        await signInWithEmailAndPassword(this.getIsolatedFirebase().auth, this.loginEmail, this.loginPassword);
      } catch (e) {
        this.authError = "Login fehlgeschlagen.";
      } finally {
        this.authLoading = false;
      }
    },
    async handleLogout() {
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      await signOut(this.getIsolatedFirebase().auth);
    },
    /** Log-Dokument normalisieren (alte Einträge haben nur die Grundfelder) */
    mapLog(doc) {
      const d = doc.data();
      const ts = d.timestamp && d.timestamp.toDate ? d.timestamp.toDate().getTime() : d.timestamp ? new Date(d.timestamp).getTime() : 0;
      const status = ["success", "warn", "error", "rejected"].includes(d.status) ? d.status : "error";
      return {
        id: doc.id,
        ...d,
        ts,
        status,
        fallback: !!d.fallback || d.engineId === "atlas-nsfw-fallback",
        _fresh: this.seenIds.size > 0 && !this.seenIds.has(doc.id) && this.selectedDate === this.todayKey,
      };
    },
    inDay(dateKey) {
      return (l) => l.ts && dayKeyOf(new Date(l.ts)) === dateKey;
    },
    /** Anzahl Logs im Zeitfenster über beide UTC-Docs (Aggregat, kein Dokument-Download) */
    async countRange(db, dateKey, start, end, extraWhere = null) {
      let n = 0;
      for (const k of utcKeysFor(dateKey)) {
        const parts = [where("timestamp", ">=", Timestamp.fromMillis(start)), where("timestamp", "<", Timestamp.fromMillis(end))];
        if (extraWhere) parts.push(extraWhere);
        try {
          const snap = await getCountFromServer(query(collection(db, "generations", k, "logs"), ...parts));
          n += snap.data().count;
        } catch (e) {
          console.error("Generation-Monitor Zählung", k, e);
        }
      }
      return n;
    },
    /** Gesamt- und Stundenzahlen des Tages, Vortag nur gesamt + Probleme */
    async loadAggregates() {
      const { db } = this.getIsolatedFirebase();
      const day = this.selectedDate;
      const { start, end } = dayBounds(day);
      const hourJobs = Array.from({ length: 24 }, (_, h) => this.countRange(db, day, start + h * 3600000, start + (h + 1) * 3600000));
      const [total, ...byHour] = await Promise.all([this.countRange(db, day, start, end), ...hourJobs]);
      if (this.selectedDate !== day) return;
      this.totals = { ...this.totals, total, byHour };
      const prev = new Date(day + "T12:00:00");
      prev.setDate(prev.getDate() - 1);
      const prevKey = dayKeyOf(prev);
      const pb = dayBounds(prevKey);
      const prevIssues = await this.loadIssues(prevKey);
      const prevTotal = await this.countRange(db, prevKey, pb.start, pb.end);
      if (this.selectedDate !== day) return;
      this.prevTotals = {
        total: prevTotal,
        issues: prevIssues.length,
        errors: prevIssues.filter((l) => l.status === "error").length,
        warn: prevIssues.filter((l) => l.status === "warn").length,
        rejected: prevIssues.filter((l) => l.status === "rejected").length,
      };
    },
    /** Alle Problem-Logs (warn/error/rejected) eines Berliner Tages — kleine Menge, vollständig */
    async loadIssues(dateKey) {
      const { db } = this.getIsolatedFirebase();
      const out = [];
      for (const k of utcKeysFor(dateKey)) {
        try {
          const snap = await getDocs(query(collection(db, "generations", k, "logs"), where("status", "in", ISSUE_STATUSES)));
          snap.docs.forEach((d) => out.push(this.mapLog(d)));
        } catch (e) {
          console.error("Generation-Monitor Probleme", k, e);
        }
      }
      return out.filter(this.inDay(dateKey));
    },
    /** Stichprobe: die neuesten SAMPLE_LIMIT je UTC-Doc */
    async loadSample(dateKey) {
      const { db } = this.getIsolatedFirebase();
      const out = [];
      for (const k of utcKeysFor(dateKey)) {
        try {
          const snap = await getDocs(query(collection(db, "generations", k, "logs"), orderBy("timestamp", "desc"), limit(SAMPLE_LIMIT)));
          snap.docs.forEach((d) => out.push(this.mapLog(d)));
        } catch (e) {
          console.error("Generation-Monitor Stichprobe", k, e);
        }
      }
      return out.filter(this.inDay(dateKey));
    },
    /** Tag laden; heute zusätzlich live: Stichprobe + Probleme per Listener, Aggregate jede Minute */
    async subscribe() {
      if (!this.authUser) return;
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      clearInterval(this.aggTimer);
      this.loading = true;
      this.seenIds = new Set();
      this.sample = [];
      this.issues = [];
      this.totals = { total: 0, byHour: Array(24).fill(0), avgMs: 0 };
      const { db } = this.getIsolatedFirebase();
      const day = this.selectedDate;
      this.loadAggregates();
      if (day !== this.todayKey) {
        const [sample, issues] = await Promise.all([this.loadSample(day), this.loadIssues(day)]);
        if (this.selectedDate !== day) return;
        this.sample = sample;
        this.issues = issues;
        this.loading = false;
        return;
      }
      this.liveBuckets = {};
      this.issueBuckets = {};
      const inDay = this.inDay(day);
      const unsubs = [];
      for (const k of utcKeysFor(day)) {
        unsubs.push(
          onSnapshot(
            query(collection(db, "generations", k, "logs"), orderBy("timestamp", "desc"), limit(SAMPLE_LIMIT)),
            (snap) => {
              this.liveBuckets[k] = snap.docs.map((d) => this.mapLog(d)).filter(inDay);
              const merged = Object.values(this.liveBuckets).flat();
              this.sample = merged;
              merged.forEach((l) => this.seenIds.add(l.id));
              this.loading = false;
              setTimeout(() => this.sample.forEach((l) => (l._fresh = false)), 4000);
            },
            (err) => {
              console.error("Generation-Monitor Listener", k, err);
              this.loading = false;
            }
          )
        );
        unsubs.push(
          onSnapshot(
            query(collection(db, "generations", k, "logs"), where("status", "in", ISSUE_STATUSES)),
            (snap) => {
              this.issueBuckets[k] = snap.docs.map((d) => this.mapLog(d)).filter(inDay);
              this.issues = Object.values(this.issueBuckets).flat();
            },
            (err) => console.error("Generation-Monitor Probleme live", k, err)
          )
        );
      }
      this.unsubscribe = () => unsubs.forEach((u) => u());
      this.aggTimer = setInterval(() => this.loadAggregates(), 60000);
    },
    summarize(list) {
      const durs = list.map((l) => l.durationMs).filter((v) => typeof v === "number" && v > 0).sort((a, b) => a - b);
      return {
        total: list.length,
        success: list.filter((l) => l.status === "success").length,
        warn: list.filter((l) => l.status === "warn").length,
        error: list.filter((l) => l.status === "error").length,
        rejected: list.filter((l) => l.status === "rejected").length,
        cutoutFailed: list.filter((l) => l.cutout && l.cutout.state === "failed").length,
        combined: list.filter((l) => l.combined).length,
        users: new Set(list.map((l) => l.userId)).size,
        p50: durs.length ? durs[Math.floor(durs.length / 2)] : 0,
        p95: durs.length ? durs[Math.min(durs.length - 1, Math.floor(durs.length * 0.95))] : 0,
      };
    },
    normalizeError(msg) {
      return String(msg)
        .replace(/[0-9a-f]{8}-[0-9a-f-]{27}/gi, "<id>")
        .replace(/\d{3,}/g, "<n>")
        .replace(/\s+/g, " ")
        .slice(0, 140);
    },
    countByStatus(s) {
      if (s === "all") return this.totals.total;
      if (s === "success") return Math.max(0, this.totals.total - this.issues.length);
      return this.issueCounts[s] || 0;
    },
    applyFilter(patch) {
      Object.assign(this.filters, patch);
      this.$vuetify.goTo && this.$el.querySelector(".gm__filters") && this.$vuetify.goTo(this.$el.querySelector(".gm__filters"), { offset: 80 });
    },
    toggleFilter(key, value) {
      this.$set(this.filters, key, this.filters[key] === value ? null : value);
    },
    clearFilter(key) {
      this.$set(this.filters, key, key === "status" ? "all" : null);
    },
    resetFilters() {
      this.filters = { status: "all", styleId: null, version: null, engine: null, cutout: null, user: null, cluster: null, hour: null, search: "" };
    },
    shiftDay(n) {
      const d = new Date(this.selectedDate + "T12:00:00");
      d.setDate(d.getDate() + n);
      const k = dayKeyOf(d);
      if (k <= this.todayKey) this.selectedDate = k;
    },
    async openDetail(item) {
      this.detail = item;
      this.loadUserSession(item);
      this.resultView = "final";
      this.resultBg = item.cutout && item.cutout.state === "done" ? "dark" : "light";
      this.promptOpen = false;
      this.drawer = true;
      this.cartState = "…";
      this.userSession = [];
      if (!item.docId) {
        this.cartState = "unbekannt (älterer Eintrag)";
        return;
      }
      try {
        const { db } = this.getIsolatedFirebase();
        const snap = await getDocs(query(collection(db, "customizations"), where("artworkId", "==", item.docId), limit(3)));
        if (this.detail !== item) return;
        this.cartState = snap.empty ? "nicht in den Warenkorb gelegt" : `${snap.size}× in den Warenkorb gelegt`;
      } catch (e) {
        this.cartState = "keine Berechtigung";
      }
    },
    /** Alle Generierungen des Nutzers am gewählten Tag (exakt, nicht nur Stichprobe) */
    async loadUserSession(item) {
      if (!item.userId) return;
      const { db } = this.getIsolatedFirebase();
      const day = this.selectedDate;
      const out = [];
      for (const k of utcKeysFor(day)) {
        try {
          const snap = await getDocs(query(collection(db, "generations", k, "logs"), where("userId", "==", item.userId)));
          snap.docs.forEach((d) => out.push(this.mapLog(d)));
        } catch (e) {
          console.error("Generation-Monitor Sitzung", e);
        }
      }
      if (this.detail !== item) return;
      this.userSession = out.filter(this.inDay(day)).sort((a, b) => (a.ts || 0) - (b.ts || 0));
    },
    inputUrls(item) {
      const arr = Array.isArray(item.inputImageUrls) ? item.inputImageUrls.filter(Boolean) : [];
      return arr.length ? arr : item.originalImageUrl ? [item.originalImageUrl] : [];
    },
    cutoutRows(item) {
      const c = item.cutout || {};
      const rows = [];
      const add = (k, v, bad) => v !== undefined && v !== null && rows.push({ k, v, bad });
      add("Methode", c.method);
      add("Drift", c.driftPct !== undefined ? c.driftPct + " %" : undefined, c.driftPct > 35);
      add("Innen geschützt", c.protectedPct !== undefined ? c.protectedPct + " %" : undefined);
      add("Papier-Regel", c.paperPct !== undefined && c.paperPct !== null ? `${c.paperPct} % Papier · ${c.guardRaisedPct} % angehoben${c.guardSkipped ? " · übersprungen" : ""}` : undefined);
      add("Deckend / halb / transparent", c.opaquePct !== undefined ? `${c.opaquePct} / ${c.semiPct} / ${c.transparentPct} %` : undefined);
      add("Größe", c.width ? `${c.width} × ${c.height}` : undefined);
      return rows;
    },
    cutoutTitle(item) {
      const c = item.cutout || {};
      return [c.method && `Methode ${c.method}`, c.driftPct !== undefined && `Drift ${c.driftPct} %`, c.guardRaisedPct !== undefined && c.guardRaisedPct !== null && `Papier-Regel ${c.guardRaisedPct} %`].filter(Boolean).join(" · ");
    },
    statusLabel(s) {
      return STATUS[s] || s;
    },
    deltaClass(k) {
      if (!k.delta) return "";
      const good = k.invert ? k.delta < 0 : k.delta > 0;
      return good ? "gm__delta--good" : "gm__delta--bad";
    },
    shortEngine(e) {
      if (!e) return "–";
      return String(e).replace("gpt-image-", "gpt-").replace("gemini-nano-", "nano-").replace("atlas-nsfw-fallback", "atlas (NSFW)");
    },
    shortId(id) {
      return id ? String(id).slice(0, 6) : "–";
    },
    hostOf(o) {
      try {
        return new URL(o).host.replace("www.", "");
      } catch (e) {
        return o;
      }
    },
    fmtMs(ms) {
      if (!ms) return "–";
      return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)} s`;
    },
    fmtTime(ts) {
      if (!ts || !isFinite(ts)) return "–";
      return new Date(ts).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
    },
    fmtDateTime(ts) {
      if (!ts) return "–";
      return new Date(ts).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: TZ });
    },
    fmtCategory(c) {
      return String(c || "").replace("HARM_CATEGORY_", "").replace(/_/g, " ").toLowerCase();
    },
    openImage(u) {
      if (!u) return;
      this.activeImg = u;
      this.imageDialog = true;
    },
  },
};
</script>

<style lang="scss" scoped>
$accent: #7b5cd6;
$shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);

.gm {
  padding: 16px 20px 40px;
  background: #f4f5f8;
  min-height: 100vh;
  font-size: 13px;

  &__login {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 70vh;
  }
  &__loginCard {
    width: 380px;
    padding: 28px;
    border-radius: 16px;
    background: #fff;
    box-shadow: $shadow;
  }
  &__loginTitle {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 800;
    margin-bottom: 4px;
  }
  &__loginHint {
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 16px;
  }
  &__loginError {
    color: #c62828;
    margin-top: 8px;
  }

  &__top {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
  }
  &__live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 999px;
    background: #eef0f4;
    color: rgba(0, 0, 0, 0.55);

    &--on {
      background: #e3f3ea;
      color: #2f7d52;
      .gm__dot {
        background: #2f7d52;
        animation: gm-pulse 1.6s ease-in-out infinite;
      }
    }
  }
  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.3);
  }
  @keyframes gm-pulse {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(47, 125, 82, 0.5);
    }
    50% {
      box-shadow: 0 0 0 6px rgba(47, 125, 82, 0);
    }
  }
  &__muted {
    color: rgba(0, 0, 0, 0.5);
    font-size: 12px;
  }
  &__day {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: #fff;
    border-radius: 999px;
    padding: 2px;
    box-shadow: $shadow;
  }
  &__dayBtn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      background: #f0f0f4;
    }
    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }
  &__dayLabel {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    font-weight: 700;
    border-radius: 999px;
    cursor: pointer;
  }
  &__dayToday {
    padding: 4px 12px;
    border-radius: 999px;
    background: #edeafa;
    color: #6a4fc4;
    font-weight: 700;
    cursor: pointer;
  }

  &__alerts {
    display: grid;
    gap: 6px;
    margin-bottom: 12px;
  }
  &__alert {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    border-radius: 12px;
    font-weight: 600;
    background: #fff4e0;
    color: #8a5a00;
    box-shadow: inset 4px 0 0 #f0a000;

    &--error {
      background: #fbe4e4;
      color: #9b1c1c;
      box-shadow: inset 4px 0 0 #c62828;
    }
  }
  &__alertBtn {
    margin-left: auto;
    padding: 3px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.7);
    font-weight: 700;
    cursor: pointer;
  }

  &__kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }
  &__kpi {
    background: #fff;
    border-radius: 14px;
    padding: 12px 14px;
    box-shadow: $shadow;
    cursor: pointer;
    transition: transform 0.12s;

    &:hover {
      transform: translateY(-1px);
    }
    &--good {
      box-shadow: $shadow, inset 0 -3px 0 #2f7d52;
    }
    &--warn {
      box-shadow: $shadow, inset 0 -3px 0 #f0a000;
    }
    &--bad {
      box-shadow: $shadow, inset 0 -3px 0 #c62828;
    }
  }
  &__kpiLabel {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 700;
  }
  &__kpiValue {
    font-size: 26px;
    font-weight: 800;
    line-height: 1.2;
    margin: 2px 0;
  }
  &__kpiUnit {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.5);
    margin-left: 2px;
  }
  &__kpiFoot {
    font-size: 11.5px;
    min-height: 16px;
  }
  &__delta {
    font-weight: 700;
    color: rgba(0, 0, 0, 0.55);

    &--good {
      color: #2f7d52;
    }
    &--bad {
      color: #c62828;
    }
  }

  &__row {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: 10px;
    margin-bottom: 12px;

    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }
  }
  &__card {
    background: #fff;
    border-radius: 14px;
    padding: 12px 14px;
    box-shadow: $shadow;
  }
  &__cardHead {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }
  &__bars {
    display: flex;
    align-items: flex-end;
    gap: 3px;
    height: 120px;
  }
  &__hour {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 100%;
    cursor: pointer;

    &:hover .gm__hourStack {
      filter: brightness(0.92);
    }
  }
  &__hourStack {
    display: flex;
    flex-direction: column;
    min-height: 2px;
    border-radius: 4px 4px 0 0;
    overflow: hidden;
    background: #eef0f4;
  }
  &__seg {
    &--ok {
      background: #c9bdf0;
    }
    &--warn {
      background: #f5c56b;
    }
    &--error {
      background: #e57373;
    }
    &--rejected {
      background: #9aa3b2;
    }
  }
  &__hourLbl {
    font-size: 10px;
    color: rgba(0, 0, 0, 0.4);
    text-align: center;
    height: 14px;

    &--now {
      color: $accent;
      font-weight: 800;
    }
  }
  &__distGroup {
    margin-bottom: 8px;
  }
  &__distTitle {
    font-size: 11px;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 700;
    margin-bottom: 4px;
  }
  &__distChips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  &__distChip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 8px 2px 10px;
    border-radius: 999px;
    background: #f1f2f6;
    font-size: 12px;
    cursor: pointer;

    &--bad {
      background: #fbe4e4;
    }
    &--on {
      background: #edeafa;
      color: #6a4fc4;
      font-weight: 700;
    }
  }
  &__distCount {
    font-weight: 700;
  }
  &__distErr {
    color: #c62828;
    font-weight: 700;
    font-size: 11px;
  }

  &__clusters {
    margin-bottom: 12px;
  }
  &__clusterList {
    display: grid;
    gap: 4px;
  }
  &__cluster {
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-radius: 10px;
    background: #fafbfc;
    text-align: left;
    cursor: pointer;

    &:hover,
    &--on {
      background: #edeafa;
    }
  }
  &__clusterCode {
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11.5px;
    color: #6a4fc4;
  }
  &__clusterMsg {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__clusterMeta {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
    white-space: nowrap;
  }

  &__filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
  }
  &__segmented {
    display: inline-flex;
    gap: 2px;
    padding: 2px;
    border-radius: 999px;
    background: #e6e8ee;

    &--mini {
      margin-left: 8px;
      vertical-align: middle;
      .gm__segBtn {
        padding: 1px 8px;
        font-size: 10.5px;
      }
    }
  }
  &__segBtn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    cursor: pointer;

    &--on {
      background: #fff;
      color: #282828;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }
  &__segCount {
    font-size: 10.5px;
    padding: 0 6px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.06);
  }
  &__search {
    max-width: 360px;
    flex: 1;
  }
  &__activeFilters {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  &__count {
    margin-left: auto;
  }
  &__linkBtn {
    color: #6a4fc4;
    font-weight: 700;
    font-size: 12px;
    cursor: pointer;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    background: #f1f2f6;
    font-size: 11.5px;
    white-space: nowrap;

    &--warn {
      background: #fff4e0;
      color: #8a5a00;
    }
    &--filter {
      background: #edeafa;
      color: #6a4fc4;
      font-weight: 700;
    }
  }
  &__chipX {
    margin-left: 2px;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
  }
  &__pill {
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    white-space: nowrap;

    &--success {
      background: #e3f3ea;
      color: #2f7d52;
    }
    &--warn {
      background: #fff4e0;
      color: #8a5a00;
    }
    &--error {
      background: #fbe4e4;
      color: #c62828;
    }
    &--rejected {
      background: #e9ebf0;
      color: #4a5261;
    }
  }

  &__loading,
  &__empty {
    padding: 40px;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
  }
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
    gap: 10px;
  }
  &__tile {
    background: #fff;
    border-radius: 14px;
    padding: 10px 12px;
    box-shadow: $shadow;
    cursor: pointer;
    transition: transform 0.12s, box-shadow 0.12s;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(16, 24, 40, 0.08), 0 8px 20px rgba(16, 24, 40, 0.1);
    }
    &--warn {
      box-shadow: $shadow, inset 4px 0 0 #f0a000;
    }
    &--error {
      box-shadow: $shadow, inset 4px 0 0 #c62828;
    }
    &--rejected {
      box-shadow: $shadow, inset 4px 0 0 #9aa3b2;
    }
    &--new {
      animation: gm-in 0.6s ease-out;
    }
  }
  @keyframes gm-in {
    from {
      background: #edeafa;
      transform: translateY(-4px);
    }
    to {
      background: #fff;
      transform: none;
    }
  }
  &__tileHead {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }
  &__time {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
    font-variant-numeric: tabular-nums;
  }
  &__tileImgs {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__inputs {
    position: relative;
    width: 110px;
    height: 110px;
    display: grid;
    gap: 2px;
    border-radius: 10px;
    overflow: hidden;
    background: #f1f2f6;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: zoom-in;
    }
    &--2 {
      grid-template-columns: 1fr 1fr;
    }
    &--3,
    &--4 {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr;
    }
    &--3 img:first-child {
      grid-row: span 2;
    }
  }
  &__combined {
    position: absolute;
    left: 4px;
    bottom: 4px;
    padding: 1px 6px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
  }
  &__arrow {
    color: rgba(0, 0, 0, 0.3) !important;
  }
  &__result {
    flex: 1;
    height: 110px;
    border-radius: 10px;
    overflow: hidden;
    background: #f1f2f6;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: zoom-in;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    &--dark {
      background: #1f2937;
    }
  }
  &__noResult {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
  }
  &__tileErr {
    margin-top: 8px;
    padding: 6px 8px;
    border-radius: 8px;
    background: #fbe4e4;
    color: #9b1c1c;
    font-size: 11.5px;
    line-height: 1.35;
    max-height: 48px;
    overflow: hidden;
  }
  &__tileFoot {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.6);
  }
  &__meta {
    display: inline-flex;
    align-items: center;
    gap: 3px;

    .v-icon {
      color: rgba(0, 0, 0, 0.4) !important;
    }
    &--bad {
      color: #c62828;
      font-weight: 700;
      .v-icon {
        color: #c62828 !important;
      }
    }
  }
  &__user {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 8px;
    border-radius: 999px;
    background: #f1f2f6;
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11px;
    cursor: pointer;

    &:hover {
      background: #edeafa;
    }
  }
  &__userN {
    margin-left: 2px;
    padding: 0 5px;
    border-radius: 999px;
    background: #edeafa;
    color: #6a4fc4;
    font-weight: 800;
    font-family: inherit;
  }
  &__more {
    text-align: center;
    padding: 14px;
  }

  // Detail
  &__detail {
    padding: 16px 18px 30px;
    font-size: 13px;
  }
  &__detailHead {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 15px;
  }
  &__detailErr {
    display: grid;
    gap: 2px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fbe4e4;
    color: #9b1c1c;
    margin-bottom: 12px;
    word-break: break-word;
  }
  &__detailImgs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 14px;
  }
  &__detailLbl {
    display: flex;
    align-items: center;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 700;
    margin-bottom: 6px;
    min-height: 22px;
  }
  &__detailInputs {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 6px;

    img {
      width: 100%;
      border-radius: 10px;
      cursor: zoom-in;
      background: #f1f2f6;
    }
  }
  &__detailResult {
    border-radius: 10px;
    overflow: hidden;
    min-height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: zoom-in;

    img {
      width: 100%;
      max-height: 460px;
      object-fit: contain;
    }
    &--dark {
      background: #1f2937;
    }
    &--light {
      background: #f1f2f6;
    }
  }
  &__section {
    margin-top: 14px;
  }
  &__sectionTitle {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 800;
    margin-bottom: 6px;
  }
  &__timeline {
    display: grid;
    gap: 4px;
  }
  &__step {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 10px;
    background: #fafbfc;

    &--fail {
      background: #fff6f6;
    }
  }
  &__stepDot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #2f7d52;

    .gm__step--fail & {
      background: #c62828;
    }
  }
  &__stepEngine {
    font-weight: 700;
    font-family: ui-monospace, Menlo, monospace;
    font-size: 12px;
  }
  &__stepErr {
    color: #9b1c1c;
    flex-basis: 100%;
    font-size: 12px;
  }
  &__stepOk {
    color: #2f7d52;
    font-weight: 600;
  }
  &__statGrid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 6px;
  }
  &__stat {
    display: grid;
    gap: 1px;
    padding: 6px 10px;
    border-radius: 10px;
    background: #fafbfc;
  }
  &__statK {
    font-size: 10.5px;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 700;
  }
  &__statV {
    font-weight: 600;
    word-break: break-all;

    &--bad {
      color: #c62828;
    }
  }
  &__mono {
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11.5px;
  }
  &__session {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    padding-bottom: 4px;
  }
  &__sessionItem {
    flex: 0 0 auto;
    width: 64px;
    border-radius: 8px;
    overflow: hidden;
    background: #f1f2f6;
    cursor: pointer;
    box-shadow: inset 0 -3px 0 #2f7d52;

    img {
      width: 64px;
      height: 64px;
      object-fit: cover;
      display: block;
    }
    &--warn {
      box-shadow: inset 0 -3px 0 #f0a000;
    }
    &--error,
    &--rejected {
      box-shadow: inset 0 -3px 0 #c62828;
    }
    &--on {
      outline: 2px solid $accent;
    }
  }
  &__sessionNo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    color: #c62828;
  }
  &__sessionLbl {
    display: block;
    font-size: 10px;
    text-align: center;
    padding: 2px 0 4px;
    color: rgba(0, 0, 0, 0.55);
  }
  &__safety {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  &__safety--high {
    background: #fbe4e4;
    color: #c62828;
  }
  &__safety--medium {
    background: #fff4e0;
    color: #8a5a00;
  }
  &__prompt {
    margin-top: 6px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fafbfc;
    font-size: 11.5px;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 320px;
    overflow: auto;
  }
  &__imgWrap {
    display: flex;
    justify-content: center;
    cursor: zoom-out;

    img {
      max-width: 92vw;
      max-height: 90vh;
      border-radius: 12px;
      background: #fff;
    }
  }
}
</style>
